import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ArrowLeftOutlined,
    RedoOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Flex, Form, Input, Button, Divider, Typography } from '@app/ui';
import connect from '@app/utils/connect';
import userActions from '@app/actions/user';
import { User } from '@app/types/api';

export type ForgottenPasswordFormProps = {
    user: User;
    backToLogin: () => void;
    sendPasswordRecoveryMail: (email: string) => void;
    sendNotification: (msg: string, opts?: any) => void;
    reInitState: () => void;
};

export const ForgottenPasswordForm = ({
    backToLogin,
    sendPasswordRecoveryMail,
    sendNotification,
    reInitState,
    user,
}: ForgottenPasswordFormProps) => {
    const { t } = useTranslation('login');
    const [loadingRequestLink, setLoadingRequestLink] = useState(false);
    const [form] = Form.useForm();

    const { isRequestingLink, error } = user;

    const hasError = useMemo(() => {
        return error && typeof error === 'object' && !!error.message;
    }, [error]);

    const hasSsoError = useMemo(() => {
        return error && typeof error === 'object' && error.ssoError;
    }, [error]);

    const sendEmailLink = async (values: { email: string }) => {
        setLoadingRequestLink(true);

        const { email } = values;
        await sendPasswordRecoveryMail(email);
    };

    useEffect(() => {
        if (!isRequestingLink && !hasError && loadingRequestLink) {
            sendNotification(t('forgottenPassword.successMessage'), {
                context: 'success',
            });

            backToLogin();
        }
    }, [isRequestingLink, hasError, loadingRequestLink, t]);

    return (
        <Flex vertical className="-m-t-2 -form">
            <Flex className="-description">
                <section>
                    <Typography.Paragraph>
                        {t('forgottenPassword.description')}
                    </Typography.Paragraph>
                </section>
            </Flex>
            <Form
                name="login"
                form={form}
                layout="vertical"
                autoComplete="off"
                onFinish={sendEmailLink}
            >
                <Form.Item
                    label={t('forgottenPassword.emailLabel')}
                    name="email"
                    validateTrigger="onBlur"
                    validateStatus={
                        hasError && !hasSsoError ? 'error' : undefined
                    }
                    help={
                        hasError && !hasSsoError && error.message
                            ? error.message
                            : undefined
                    }
                    rules={[
                        {
                            type: 'email',
                            message: t(
                                'forgottenPassword.validation.emailInvalid',
                            ),
                        },
                        {
                            min: 3,
                            message: t(
                                'forgottenPassword.validation.emailRequired',
                            ),
                        },
                    ]}
                >
                    <Input
                        placeholder={t('forgottenPassword.emailPlaceholder')}
                        type="email"
                        prefix={<UserOutlined />}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        icon={<RedoOutlined />}
                        loading={
                            (isRequestingLink || loadingRequestLink) &&
                            !hasError &&
                            !hasSsoError
                        }
                        disabled={isRequestingLink}
                        iconPosition="end"
                        htmlType="submit"
                    >
                        {t('forgottenPassword.resetLink')}
                    </Button>
                </Form.Item>

                <Divider />

                <Button
                    onClick={() => {
                        reInitState();
                        backToLogin();
                    }}
                    type="link"
                    icon={
                        <ArrowLeftOutlined
                            style={{ color: 'rgba(0, 159, 223, 1)' }}
                        />
                    }
                    iconPosition="start"
                >
                    {t('forgottenPassword.backToSignIn')}
                </Button>
            </Form>
        </Flex>
    );
};

export default connect(['user'], userActions)(ForgottenPasswordForm);
